/**
 * TabelloneCL — tabellone ad albero della fase a eliminazione Champions.
 *
 * Andrea 2026-09-09: prima la fase KO era una lista di partite (e prima
 * del sorteggio un cartello "non ancora generata"). Ora c'è il tabellone
 * classico, che esiste da subito:
 *
 *   1. la STRUTTURA arriva dal piano giornate KO (`/piano-ko`), che dà
 *      anche gli accoppiamenti generici — "1ª Girone A vs 4ª Girone B"
 *   2. finché i gironi non sono finiti si legge il posto generico, con
 *      sotto la squadra che lo occuperebbe adesso (proiezione live)
 *   3. a gironi finiti / partite KO sorteggiate compaiono i nomi veri, e
 *      i vincenti scalano di turno fino alla coppa in fondo
 *
 * Il tabellone non si fida dell'ordine con cui l'API restituisce le
 * partite (ordinate per giornata/id): ogni accoppiamento reale viene
 * agganciato al suo posto confrontando la COPPIA di squadre con quella
 * prevista dalla struttura (1A-4B, 2A-3B, 2B-3A, 1B-4A).
 */

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { usePianoKO, type GironeItem, type PartitaItem } from "../../hooks/useFase5";
import { CoppaChampions } from "../common/CoppaChampions";
import { MagliaSquadra } from "../common/MagliaSquadra";
import { classificaDaPartite } from "../common/classifica";
import { tr } from "../../i18n";

type Round = "quarti" | "semi" | "finale";

const ROUND_ORDER: Round[] = ["quarti", "semi", "finale"];

const ROUND_LABEL: Record<Round, string> = {
  quarti: tr("Quarti di finale"),
  semi: tr("Semifinali"),
  finale: tr("Finale"),
};

/** Fasi partita che appartengono a ogni turno del tabellone. */
const ROUND_FASI: Record<Round, string[]> = {
  quarti: ["quarti_andata", "quarti_ritorno"],
  semi: ["semi_andata", "semi_ritorno"],
  finale: ["finale_andata", "finale_ritorno", "finale"],
};

interface Posto {
  /** null = ancora da assegnare. */
  squadraId: string | null;
  /** Etichetta generica del posto ("1ª Girone A", "Vincente quarto 1"). */
  generica: string;
  /** true = squadra certa (partita sorteggiata o girone concluso). */
  certa: boolean;
}

interface Accoppiamento {
  key: string;
  casa: Posto;
  trasferta: Posto;
  /** Gol aggregati (andata + ritorno) quando le partite sono calcolate. */
  golCasa: number | null;
  golTrasferta: number | null;
  vincitore: string | null;
  /** Aggregato in parità: decide lo spareggio, non il tabellone. */
  parita: boolean;
  legs: PartitaItem[];
  /** Gare del confronto con i gol orientati come il box (casa in alto). */
  gare: Array<{
    id: string;
    giocata: boolean;
    golCasa: number;
    golTrasferta: number;
  }>;
}

interface TabelloneCLProps {
  competizioneId: string;
  gironi: GironeItem[] | undefined;
  partite: PartitaItem[];
  nomeSquadra: (id: string) => string;
}

function postoVuoto(generica: string): Posto {
  return { squadraId: null, generica, certa: false };
}

export function TabelloneCL({ competizioneId, gironi, partite, nomeSquadra }: TabelloneCLProps) {
  const { data: pianoKO } = usePianoKO(competizioneId);

  const { turni, campione, finaleGiocata } = useMemo(() => {
    // --- classifiche dei gironi (proiezione live) ---
    const classifichePerGirone = new Map<string, ReturnType<typeof classificaDaPartite>>();
    let gironiCompleti = (gironi?.length ?? 0) > 0;
    for (const g of gironi ?? []) {
      const fase = g.nome === "A" ? "girone_a" : "girone_b";
      const partiteGirone = partite.filter((p) => p.fase === fase);
      classifichePerGirone.set(
        g.nome,
        classificaDaPartite(
          g.partecipanti.map((p) => p.squadra_id),
          partiteGirone,
          nomeSquadra,
        ),
      );
      if (partiteGirone.length === 0 || partiteGirone.some((p) => p.punteggio_casa === null))
        gironiCompleti = false;
    }

    /** "1ª Girone A" → squadra che occupa quel posto adesso. */
    const daEtichettaGirone = (etichetta: string): Posto => {
      const m = /^(\d+)ª\s+Girone\s+([A-Z])$/.exec(etichetta.trim());
      if (!m) return postoVuoto(etichetta);
      const riga = classifichePerGirone.get(m[2])?.[Number(m[1]) - 1];
      return {
        squadraId: riga?.squadra_id ?? null,
        generica: etichetta,
        certa: gironiCompleti && !!riga,
      };
    };

    // --- turni previsti dal piano (o dedotti dalle partite esistenti) ---
    const fasiPiano = new Set((pianoKO ?? []).map((t) => t.fase));
    const fasiReali = new Set(
      partite
        .map((p) => ROUND_ORDER.find((r) => ROUND_FASI[r].includes(p.fase)))
        .filter(Boolean) as Round[],
    );
    // Il tabellone si mostra SEMPRE intero (semi e finale comprese anche
    // se non sorteggiate): l'unica variabile e' se si parte dai quarti
    // (qualificate=4) o direttamente dalle semifinali (qualificate=2).
    const conQuarti =
      fasiPiano.has("quarti") ||
      fasiReali.has("quarti") ||
      // CL vecchie senza piano e senza KO: gironi da 4 = quarti.
      (fasiPiano.size === 0 &&
        fasiReali.size === 0 &&
        (gironi?.[0]?.partecipanti.length ?? 0) >= 4);
    // Col piano giornate si mostrano solo i turni previsti (col girone
    // unico e 2 qualificate c'e' la sola finale).
    const rounds: Round[] = fasiPiano.size
      ? ROUND_ORDER.filter((r) => fasiPiano.has(r))
      : conQuarti
        ? ROUND_ORDER
        : (["semi", "finale"] as Round[]);

    const accoppiamentiPrevisti = (r: Round): string[] => {
      const turno = (pianoKO ?? []).find((t) => t.fase === r);
      if (turno) return turno.accoppiamenti;
      // Fallback (CL vecchie senza piano): struttura standard.
      if (r === "quarti")
        return [
          tr("1ª Girone A vs 4ª Girone B"),
          tr("2ª Girone A vs 3ª Girone B"),
          tr("2ª Girone B vs 3ª Girone A"),
          tr("1ª Girone B vs 4ª Girone A"),
        ];
      if (r === "semi")
        return rounds.includes("quarti")
          ? [
              tr("Vincente quarto 1 vs Vincente quarto 2"),
              tr("Vincente quarto 3 vs Vincente quarto 4"),
            ]
          : [tr("1ª Girone A vs 2ª Girone B"), tr("1ª Girone B vs 2ª Girone A")];
      return [tr("Vincente semifinale 1 vs Vincente semifinale 2")];
    };

    const turni: { round: Round; accoppiamenti: Accoppiamento[] }[] = [];

    for (const round of rounds) {
      const partiteRound = partite.filter((p) => ROUND_FASI[round].includes(p.fase));
      const previsti = accoppiamentiPrevisti(round);
      const precedente = turni[turni.length - 1] ?? null;

      const accoppiamenti: Accoppiamento[] = previsti.map((testo, idx) => {
        const [sxTesto, dxTesto] = testo.split(" vs ").map((t) => t.trim());
        // Posti: dai gironi oppure dai vincenti del turno precedente.
        const daPrecedente = (i: number, generica: string): Posto => {
          const acc = precedente?.accoppiamenti[i];
          return {
            squadraId: acc?.vincitore ?? null,
            generica,
            certa: !!acc?.vincitore,
          };
        };
        let casa: Posto;
        let trasferta: Posto;
        if (precedente) {
          casa = daPrecedente(idx * 2, sxTesto ?? tr("Vincente"));
          trasferta = daPrecedente(idx * 2 + 1, dxTesto ?? tr("Vincente"));
        } else {
          casa = daEtichettaGirone(sxTesto ?? "");
          trasferta = daEtichettaGirone(dxTesto ?? "");
        }
        return {
          key: `${round}-${idx}`,
          casa,
          trasferta,
          golCasa: null,
          golTrasferta: null,
          vincitore: null,
          parita: false,
          legs: [],
          gare: [],
        };
      });

      // --- aggancia le partite vere al posto giusto del tabellone ---
      const coppieUsate = new Set<string>();
      const coppiaKey = (a: string, b: string) => [a, b].sort().join("|");
      const perCoppia = new Map<string, PartitaItem[]>();
      for (const p of partiteRound) {
        const k = coppiaKey(p.squadra_casa_id, p.squadra_trasferta_id);
        perCoppia.set(k, [...(perCoppia.get(k) ?? []), p]);
      }
      // L'API ordina per giornata_id (un UUID): l'andata non e' garantita
      // prima del ritorno. Ordiniamo per fase.
      for (const [k, legs] of perCoppia) {
        perCoppia.set(
          k,
          [...legs].sort(
            (a, b) => ROUND_FASI[round].indexOf(a.fase) - ROUND_FASI[round].indexOf(b.fase),
          ),
        );
      }
      // 1) accoppiamenti con entrambe le squadre note → match esatto
      for (const acc of accoppiamenti) {
        if (!acc.casa.squadraId || !acc.trasferta.squadraId) continue;
        const k = coppiaKey(acc.casa.squadraId, acc.trasferta.squadraId);
        const legs = perCoppia.get(k);
        if (legs && !coppieUsate.has(k)) {
          acc.legs = legs;
          coppieUsate.add(k);
        }
      }
      // 2) coppie rimaste (proiezione sbagliata / gironi non finiti) →
      //    riempiono in ordine i posti ancora liberi
      const avanzo = [...perCoppia.entries()].filter(([k]) => !coppieUsate.has(k));
      for (const acc of accoppiamenti) {
        if (acc.legs.length > 0) continue;
        const prossima = avanzo.shift();
        if (!prossima) break;
        acc.legs = prossima[1];
        coppieUsate.add(prossima[0]);
      }

      // --- aggregato, squadre certe e vincitore ---
      for (const acc of accoppiamenti) {
        if (acc.legs.length === 0) continue;
        // La squadra "casa" del tabellone è quella dell'ultima gara in
        // programma (ritorno in casa della meglio piazzata).
        const riferimento = acc.legs[acc.legs.length - 1];
        const idCasa = riferimento.squadra_casa_id;
        const idTrasferta = riferimento.squadra_trasferta_id;
        acc.casa = { ...acc.casa, squadraId: idCasa, certa: true };
        acc.trasferta = { ...acc.trasferta, squadraId: idTrasferta, certa: true };
        // Gara per gara, sempre "casa del tabellone" - "ospite del
        // tabellone": leggere 0-2 con le squadre invertite ingannava.
        acc.gare = acc.legs.map((p) => ({
          id: p.id,
          giocata: p.punteggio_casa !== null,
          golCasa: p.squadra_casa_id === idCasa ? p.gol_casa : p.gol_trasferta,
          golTrasferta: p.squadra_casa_id === idCasa ? p.gol_trasferta : p.gol_casa,
        }));
        const calcolate = acc.legs.filter((p) => p.punteggio_casa !== null);
        if (calcolate.length === acc.legs.length && calcolate.length > 0) {
          let golCasa = 0;
          let golTrasferta = 0;
          for (const p of calcolate) {
            if (p.squadra_casa_id === idCasa) {
              golCasa += p.gol_casa;
              golTrasferta += p.gol_trasferta;
            } else {
              golCasa += p.gol_trasferta;
              golTrasferta += p.gol_casa;
            }
          }
          acc.golCasa = golCasa;
          acc.golTrasferta = golTrasferta;
          if (golCasa > golTrasferta) acc.vincitore = idCasa;
          else if (golTrasferta > golCasa) acc.vincitore = idTrasferta;
          else acc.parita = true;
        }
      }

      turni.push({ round, accoppiamenti });
    }

    const ultimo = turni[turni.length - 1] ?? null;
    const finale = ultimo?.round === "finale" ? ultimo.accoppiamenti[0] : null;
    return {
      turni,
      campione: finale?.vincitore ?? null,
      finaleGiocata: !!finale && finale.legs.length > 0,
    };
  }, [gironi, partite, pianoKO, nomeSquadra]);

  if (turni.length === 0) {
    return (
      <div className="card text-center text-fg-muted">
        {tr("Nessuna fase a eliminazione prevista per questa competizione.")}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="card overflow-x-auto p-3">
        <div className="flex items-stretch gap-1 min-w-[560px]">
          {turni.map((t, i) => (
            <div key={t.round} className="flex items-stretch gap-1 flex-1">
              <div className="flex-1 flex flex-col">
                <div className="text-[10px] font-bold uppercase tracking-wider text-fg-muted text-center mb-2">
                  {ROUND_LABEL[t.round]}
                </div>
                <div className="flex-1 flex flex-col justify-around gap-2">
                  {t.accoppiamenti.map((acc) => (
                    <MatchBox key={acc.key} acc={acc} nomeSquadra={nomeSquadra} />
                  ))}
                </div>
              </div>
              {/* Graffe di collegamento verso il turno successivo */}
              {i < turni.length - 1 && <Graffe coppie={Math.max(1, t.accoppiamenti.length / 2)} />}
            </div>
          ))}

          {/* Coppa finale */}
          <div className="w-24 shrink-0 flex flex-col">
            <div className="text-[10px] font-bold uppercase tracking-wider text-fg-muted text-center mb-2">
              {tr("Campione")}
            </div>
            <div className="flex-1 flex flex-col items-center justify-center gap-1">
              <CoppaChampions size={52} vinta={!!campione} />
              <div
                className={`text-[11px] text-center font-bold leading-tight ${
                  campione ? "text-accent" : "text-fg-muted"
                }`}
              >
                {campione
                  ? nomeSquadra(campione)
                  : finaleGiocata
                    ? tr("finale in corso")
                    : tr("da assegnare")}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="text-[11px] text-fg-muted px-1">
        I posti mostrano l'etichetta del tabellone finché i gironi non sono conclusi; sotto, in
        corsivo, la squadra che lo occuperebbe adesso. Il punteggio è l'aggregato di andata e
        ritorno.
      </div>
    </div>
  );
}

/** Graffa "]" che unisce due accoppiamenti al turno successivo. */
function Graffe({ coppie }: { coppie: number }) {
  return (
    <div className="w-4 shrink-0 flex flex-col pt-6">
      {Array.from({ length: coppie }, (_, i) => (
        <div
          key={i}
          className="flex-1 border-r border-t border-b border-primary-soft rounded-r my-3"
        />
      ))}
    </div>
  );
}

function MatchBox({
  acc,
  nomeSquadra,
}: {
  acc: Accoppiamento;
  nomeSquadra: (id: string) => string;
}) {
  const box = (
    <div className="rounded border border-primary-soft bg-primary-deep/30 overflow-hidden">
      <PostoRow
        posto={acc.casa}
        gol={acc.golCasa}
        vincente={!!acc.vincitore && acc.vincitore === acc.casa.squadraId}
        nomeSquadra={nomeSquadra}
      />
      <div className="h-px bg-primary-soft/60" />
      <PostoRow
        posto={acc.trasferta}
        gol={acc.golTrasferta}
        vincente={!!acc.vincitore && acc.vincitore === acc.trasferta.squadraId}
        nomeSquadra={nomeSquadra}
      />
      {acc.parita && (
        <div className="px-1.5 py-0.5 text-[9px] text-warning bg-warning/10 text-center">
          {tr("parità: serve lo spareggio")}
        </div>
      )}
      {acc.gare.length > 0 && (
        <div className="px-1.5 py-0.5 flex gap-2 justify-center bg-primary-deep/40">
          {acc.gare.map((gara, i) => (
            <span key={gara.id} className="text-[9px] text-fg-muted font-mono">
              {acc.gare.length > 1 ? (i === 0 ? "A " : "R ") : ""}
              {gara.giocata ? `${gara.golCasa}-${gara.golTrasferta}` : "—"}
            </span>
          ))}
        </div>
      )}
    </div>
  );
  // Con le partite sorteggiate il box porta al dettaglio (ultima gara).
  const partita = acc.legs[acc.legs.length - 1];
  if (!partita) return box;
  return (
    <Link
      to={`/partita/${partita.id}`}
      className="block hover:opacity-90 transition"
      title={tr("Apri il dettaglio della partita")}
    >
      {box}
    </Link>
  );
}

function PostoRow({
  posto,
  gol,
  vincente,
  nomeSquadra,
}: {
  posto: Posto;
  gol: number | null;
  vincente: boolean;
  nomeSquadra: (id: string) => string;
}) {
  const nome = posto.squadraId ? nomeSquadra(posto.squadraId) : null;
  return (
    <div className={`flex items-center gap-1 px-1.5 py-1 ${vincente ? "bg-accent/10" : ""}`}>
      {posto.squadraId && <MagliaSquadra squadraId={posto.squadraId} size={16} />}
      <div className="flex-1 min-w-0">
        {posto.certa && nome ? (
          <>
            <div
              className={`text-[11px] font-bold truncate ${vincente ? "text-accent" : "text-fg"}`}
            >
              {nome}
            </div>
            <div className="text-[9px] text-fg-muted truncate">{posto.generica}</div>
          </>
        ) : (
          <>
            <div className="text-[11px] font-bold text-fg-muted truncate">{posto.generica}</div>
            <div className="text-[9px] text-fg-muted/80 italic truncate">
              {nome ? tr("ora: {{v1}}", { v1: nome }) : tr("da definire")}
            </div>
          </>
        )}
      </div>
      <span
        className={`w-4 text-right font-mono text-[11px] shrink-0 ${
          vincente ? "text-accent font-bold" : "text-fg-muted"
        }`}
      >
        {gol ?? ""}
      </span>
    </div>
  );
}
